// client/src/pages/About.js
import React, { useEffect, useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Avatar,
  IconButton,
  Divider,
  IconButton as MuiIconButton,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Gavel,
  Balance,
  VerifiedUser,
  AccessTime,
  LocationOn,
  Phone,
  Email,
  EmojiEvents,
  Security,
  Speed,
  ThumbUp,
  LinkedIn,
  Twitter,
  Email as EmailIcon,
  NavigateBefore,
  NavigateNext,
  Star,
  StarBorder
} from '@mui/icons-material';
import { teamMembers, achievements } from '../data/ServicesData';

const About = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [currentAchievement, setCurrentAchievement] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('fade-in-up');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.animate-on-scroll').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  // Auto-play du carrousel
  useEffect(() => {
    if (!autoPlay) return;
    const interval = setInterval(() => {
      setCurrentAchievement((prev) => (prev + 1) % achievements.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [autoPlay, achievements.length]);

  const handlePrev = () => {
    setAutoPlay(false);
    setCurrentAchievement((prev) => (prev - 1 + achievements.length) % achievements.length);
    setTimeout(() => setAutoPlay(true), 5000);
  };

  const handleNext = () => {
    setAutoPlay(false);
    setCurrentAchievement((prev) => (prev + 1) % achievements.length);
    setTimeout(() => setAutoPlay(true), 5000);
  };

  const goToSlide = (index) => {
    setAutoPlay(false);
    setCurrentAchievement(index);
    setTimeout(() => setAutoPlay(true), 5000);
  };

  const values = [
    { icon: <Security />, title: 'Éthique et Déontologie', color: '#1a237e', desc: 'Nous respectons les règles strictes de notre profession.' },
    { icon: <ThumbUp />, title: 'Honnêteté et Transparence', color: '#ff9800', desc: 'Une communication claire et sans détour avec nos clients.' },
    { icon: <Speed />, title: 'Efficacité et Professionnalisme', color: '#1a237e', desc: 'Des solutions rapides et adaptées à votre situation.' },
    { icon: <EmojiEvents />, title: 'Excellence', color: '#ff9800', desc: 'La recherche constante de la meilleure qualité de service.' },
  ];

  // Afficher 3 images visibles en desktop, 1 en mobile
  const getVisibleAchievements = () => {
    if (isMobile) {
      return [achievements[currentAchievement]];
    }
    const prev = (currentAchievement - 1 + achievements.length) % achievements.length;
    const next = (currentAchievement + 1) % achievements.length;
    return [achievements[prev], achievements[currentAchievement], achievements[next]];
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
      {/* Hero Section */}
      <Paper
        elevation={0}
        className="fade-in-up"
        sx={{
          p: { xs: 4, md: 6 },
          mb: 5,
          background: 'linear-gradient(135deg, #1a237e 0%, #283593 50%, #1a237e 100%)',
          color: 'white',
          borderRadius: 4,
          position: 'relative',
          overflow: 'hidden',
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 250,
            height: 250,
            background: 'radial-gradient(circle, rgba(255,152,0,0.2) 0%, rgba(255,152,0,0) 70%)',
            borderRadius: '50%',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -50,
            left: -50,
            width: 250,
            height: 250,
            background: 'radial-gradient(circle, rgba(255,152,0,0.2) 0%, rgba(255,152,0,0) 70%)',
            borderRadius: '50%',
          }}
        />
        <Typography 
          variant="h2" 
          gutterBottom 
          fontWeight="bold"
          sx={{ 
            fontSize: { xs: '2rem', md: '3.5rem' },
            background: 'linear-gradient(135deg, #fff, #ffb74d)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          À propos d'AUJAA GROUP
        </Typography>
        <Typography variant="h6" fontWeight="bold" sx={{ 
          background: 'linear-gradient(135deg, #fff, #ffb74d)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          mb:2,
        }}>
          Clinique Juridique - Expertise & Confiance depuis 2014
        </Typography>
        <Typography variant="body1" sx={{ maxWidth: '700px', margin: '0 auto', mt: 2, opacity: 0.9 }}>
          Votre partenaire de confiance pour tous vos besoins juridiques, administratifs et prestations au Togo et à l'international.
        </Typography>
      </Paper>

      {/* ========== NOUVEAU CARROUSEL DES RÉALISATIONS ========== */}
      <Box sx={{ mb: 6 }}>
        <Typography 
          variant="h3" 
          gutterBottom 
          align="center" 
          className="animate-on-scroll"
          sx={{ mb: 1, fontWeight: 'bold' }}
        >
          Nos Réalisations
        </Typography>
        <Typography 
          variant="body1" 
          align="center" 
          color="text.secondary" 
          sx={{ mb: 4, maxWidth: 600, margin: '0 auto' }}
        >
          Découvrez quelques-unes de nos réussites et transactions accomplies avec succès
        </Typography>

        <Box sx={{ position: 'relative', px: { xs: 0, md: 4 } }}>
          {/* Navigation buttons */}
          <MuiIconButton
            onClick={handlePrev}
            sx={{
              position: 'absolute',
              left: { xs: -10, md: -20 },
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
              backgroundColor: 'rgba(26,35,126,0.8)',
              color: 'white',
              '&:hover': { backgroundColor: '#ff9800' },
              display: { xs: 'none', md: 'flex' }
            }}
          >
            <NavigateBefore fontSize="large" />
          </MuiIconButton>

          <MuiIconButton
            onClick={handleNext}
            sx={{
              position: 'absolute',
              right: { xs: -10, md: -20 },
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
              backgroundColor: 'rgba(26,35,126,0.8)',
              color: 'white',
              '&:hover': { backgroundColor: '#ff9800' },
              display: { xs: 'none', md: 'flex' }
            }}
          >
            <NavigateNext fontSize="large" />
          </MuiIconButton>

          {/* Carrousel */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 3,
              overflow: 'hidden',
              py: 4,
            }}
          >
            {getVisibleAchievements().map((item, idx) => {
              const isCenter = !isMobile && idx === 1;
              return (
                <Box
                  key={item.id}
                  className="animate-on-scroll"
                  sx={{
                    flex: isCenter ? { xs: 1, md: 1.2 } : { xs: 1, md: 0.9 },
                    transform: isCenter ? { xs: 'none', md: 'scale(1.05)' } : 'none',
                    transition: 'all 0.5s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      '& .achievement-overlay': {
                        opacity: 1,
                      },
                    },
                  }}
                  onClick={() => {
                    const targetIndex = achievements.findIndex(a => a.id === item.id);
                    goToSlide(targetIndex);
                  }}
                >
                  <Paper
                    elevation={isCenter ? 8 : 4}
                    sx={{
                      borderRadius: 4,
                      overflow: 'hidden',
                      position: 'relative',
                      height: { xs: 300, md: isCenter ? 380 : 340 },
                      background: 'linear-gradient(135deg, #1a237e, #283593)',
                    }}
                  >
                    <img
                      src={item.image}
                      alt={item.title}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.5s ease',
                      }}
                      onError={(e) => {
                        e.target.src = '/images/placeholder.jpg';
                      }}
                    />
                    {/* Overlay avec catégorie */}
                    <Box
                      className="achievement-overlay"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.4) 50%, transparent 100%)',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-end',
                        p: 3,
                        transition: 'opacity 0.3s ease',
                      }}
                    >
                      <Box sx={{ mb: 1 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            backgroundColor: '#1a237e',
                            color: '#ff9800',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 20,
                            fontWeight: 'bold',
                            display: 'inline-block',
                            mb: 1,
                          }}
                        >
                          {item.category} • {item.year}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: { xs: '0.9rem', md: '2rem' },color: '#ff9800', fontWeight: 'bold', mb: 0.5 }}>
                        {item.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                        {item.description}
                      </Typography>
                    </Box>
                  </Paper>
                </Box>
              );
            })}
          </Box>

          {/* Indicateurs de slide (dots) */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, mt: 2 }}>
            {achievements.map((_, index) => (
              <Box
                key={index}
                onClick={() => goToSlide(index)}
                sx={{
                  width: currentAchievement === index ? 32 : 10,
                  height: 10,
                  borderRadius: 5,
                  backgroundColor: currentAchievement === index ? '#ff9800' : 'rgba(26,35,126,0.3)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: '#ff9800',
                    width: 20,
                  },
                }}
              />
            ))}
          </Box>

          {/* Statistiques des réalisations */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: { xs: 2, md: 6 },
              mt: 4,
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ color: '#1a237e', fontWeight: 'bold' }}>
                150+
              </Typography>
              <Typography variant="caption" color="text.secondary">Transactions immobilières</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ color: '#1a237e', fontWeight: 'bold' }}>
                85+
              </Typography>
              <Typography variant="caption" color="text.secondary">Dossiers de recouvrement</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ color: '#1a237e', fontWeight: 'bold' }}>
                30+
              </Typography>
              <Typography variant="caption" color="text.secondary">Appels d'offres remportés</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ color: '#1a237e', fontWeight: 'bold' }}>
                500+
              </Typography>
              <Typography variant="caption" color="text.secondary">Clients accompagnés</Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Mission & Valeurs */}
      <Grid container spacing={4} sx={{ mb: 6 }}>
        {/* ... le reste du code reste identique ... */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card className="animate-on-scroll" elevation={0} sx={{ height: '100%', borderRadius: 4, boxShadow: 2 }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar sx={{ bgcolor: '#1a237e', mr: 2, width: 56, height: 56 }}>
                  <Balance sx={{ fontSize: 32 }} />
                </Avatar>
                <Typography variant="h4" fontWeight="bold" sx={{ color: '#1a237e' }}>
                  Notre Mission
                </Typography>
              </Box>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                AUJAA GROUP a pour mission de rendre le droit accessible à tous,
                en proposant des services juridiques de qualité adaptés aux
                besoins des particuliers et des entreprises.
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                Nous œuvrons pour une justice plus protectrice des citoyens
                et plus équitable vis-à-vis des victimes, avec une approche
                humaine et professionnelle.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid size={{ xs: 12, md: 6 }}>
          <Card className="animate-on-scroll" elevation={0} sx={{ height: '100%', borderRadius: 4, boxShadow: 2 }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar sx={{ bgcolor: '#ff9800', mr: 2, width: 56, height: 56 }}>
                  <VerifiedUser sx={{ fontSize: 32 }} />
                </Avatar>
                <Typography variant="h4" fontWeight="bold" sx={{ color: '#1a237e' }}>
                  Nos Valeurs
                </Typography>
              </Box>
              <Grid container spacing={2}>
                {values.map((value, index) => (
                  <Grid size={{ xs: 12, sm: 6 }} key={index}>
                    <Box 
                      sx={{ 
                        p: 2,
                        borderRadius: 3,
                        backgroundColor: 'rgba(26,35,126,0.05)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          backgroundColor: 'rgba(26,35,126,0.1)',
                          transform: 'translateX(5px)',
                        },
                      }}
                    >
                      <Box sx={{ color: value.color, mb: 1 }}>{value.icon}</Box>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
                        {value.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {value.desc}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Section Notre Équipe */}
      <Box sx={{ mb: 6 }}>
        <Typography 
          variant="h3" 
          gutterBottom 
          align="center" 
          className="animate-on-scroll"
          sx={{ mb: 1, fontWeight: 'bold' }}
        >
          Notre Équipe
        </Typography>
        <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4, maxWidth: 600, margin: '0 auto' }}>
          Des professionnels dévoués à votre service, experts dans leurs domaines respectifs.
        </Typography>
        
        <div className="team-grid">
          {teamMembers.map((member, index) => (
            <div key={member.id} className="team-card animate-on-scroll" style={{ animationDelay: `${index * 100}ms` }}>
              <img src={member.avatar} alt={member.name} className="team-avatar" />
              <Typography className="team-name">{member.name}</Typography>
              <Typography className="team-role">{member.role}</Typography>
              <Typography className="team-bio">{member.bio}</Typography>
              <div className="team-social">
                <IconButton size="small" href={`mailto:${member.email}`} sx={{ color: '#1a237e' }}>
                  <EmailIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" href={member.linkedin} sx={{ color: '#1a237e' }}>
                  <LinkedIn fontSize="small" />
                </IconButton>
                <IconButton size="small" href={member.twitter} sx={{ color: '#1a237e' }}>
                  <Twitter fontSize="small" />
                </IconButton>
              </div>
            </div>
          ))}
        </div>
      </Box>

      {/* Notre Engagement */}
      <Card className="animate-on-scroll" elevation={0} sx={{ mb: 5, borderRadius: 4, boxShadow: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Avatar sx={{ bgcolor: '#1a237e', mr: 2, width: 56, height: 56 }}>
              <Gavel sx={{ fontSize: 32 }} />
            </Avatar>
            <Typography variant="h4" fontWeight="bold" sx={{ color: '#1a237e' }}>
              Notre Engagement
            </Typography>
          </Box>
          <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
            Notre clinique juridique réunit des juristes expérimentés, spécialisés dans divers domaines du droit. 
            Chaque membre de notre équipe partage les mêmes valeurs de rigueur, d'excellence et d'engagement envers nos clients.
          </Typography>
          <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
            Nous sommes disponibles 7j/7 pour vous accompagner dans toutes vos démarches juridiques, 
            avec une réponse rapide et personnalisée à chaque demande.
          </Typography>
        </CardContent>
      </Card>

      {/* Coordonnées */}
      <Card className="animate-on-scroll" elevation={2} sx={{ borderRadius: 4 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom fontWeight="bold" align="center" sx={{ color: '#1a237e', mb: 4 }}>
            Nous Contacter
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <LocationOn sx={{ fontSize: 48, color: '#1a237e', mb: 1 }} />
                <Typography variant="subtitle1" fontWeight="bold">Adresse</Typography>
                <Typography variant="body2" color="text.secondary">Tokoin Forever, Lomé - Togo</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Phone sx={{ fontSize: 48, color: '#ff9800', mb: 1 }} />
                <Typography variant="subtitle1" fontWeight="bold">Téléphones</Typography>
                <Typography variant="body2" color="text.secondary">+228 90 35 87 66</Typography>
                <Typography variant="body2" color="text.secondary">+228 99 54 90 41</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Email sx={{ fontSize: 48, color: '#1a237e', mb: 1 }} />
                <Typography variant="subtitle1" fontWeight="bold">Email</Typography>
                <Typography variant="body2" color="text.secondary">aujaagroup@gmail.com</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <AccessTime sx={{ fontSize: 48, color: '#ff9800', mb: 1 }} />
                <Typography variant="subtitle1" fontWeight="bold">Disponibilité</Typography>
                <Typography variant="body2" color="text.secondary">Lundi - Dimanche</Typography>
                <Typography variant="body2" color="text.secondary">8h - 20h</Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default About;